/**
 * @name HomePageDependentesGastos
 * @description Componente para exibir gastos por dependente
 * @author Gabriel M. Guimarães | gabrielmg7
 */

import { Person as PersonIcon } from '@mui/icons-material';
import { Box, Card, CardContent, LinearProgress, Typography, useTheme } from '@mui/material';
import React from 'react';

interface IGastosPorDependente {
     dependente_id: number;
     nome: string;
     total_gastos: number;
     percentual: number;
}

interface HomePageDependentesGastosProps {
     data: IGastosPorDependente[];
     title: string;
}

export const HomePageDependentesGastos: React.FC<HomePageDependentesGastosProps> = ({ data, title }) => {
     const theme = useTheme();

     const formatCurrency = (value: number) => {
          return new Intl.NumberFormat('pt-BR', {
               style: 'currency',
               currency: 'BRL',
          }).format(value);
     };

     const colors = [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.warning.main, theme.palette.error.main, theme.palette.info.main, theme.palette.success.main];

     return (
          <Card sx={{ height: '100%' }}>
               <CardContent sx={{ p: 3 }}>
                    <Typography
                         variant='h6'
                         gutterBottom
                         fontWeight='bold'
                    >
                         {title}
                    </Typography>

                    {data.length === 0 ? (
                         <Box
                              sx={{
                                   height: 300,
                                   display: 'flex',
                                   flexDirection: 'column',
                                   alignItems: 'center',
                                   justifyContent: 'center',
                                   gap: 2,
                              }}
                         >
                              <PersonIcon sx={{ fontSize: 48, color: theme.palette.text.secondary }} />
                              <Typography
                                   variant='body2'
                                   color='text.secondary'
                                   textAlign='center'
                              >
                                   Nenhum gasto registrado este mês
                              </Typography>
                         </Box>
                    ) : (
                         <Box sx={{ height: 300 }}>
                              {data.map((dependente, index) => (
                                   <Box
                                        key={dependente.dependente_id}
                                        sx={{
                                             mb: 3,
                                             p: 2,
                                             backgroundColor: theme.palette.background.default,
                                             borderRadius: 2,
                                             border: `1px solid ${theme.palette.divider}`,
                                        }}
                                   >
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                             <Typography
                                                  variant='subtitle2'
                                                  fontWeight='bold'
                                                  sx={{ color: colors[index % colors.length] }}
                                             >
                                                  {dependente.nome}
                                             </Typography>
                                             <Typography
                                                  variant='body2'
                                                  fontWeight='bold'
                                             >
                                                  {formatCurrency(dependente.total_gastos)}
                                             </Typography>
                                        </Box>

                                        <LinearProgress
                                             variant='determinate'
                                             value={dependente.percentual}
                                             sx={{
                                                  height: 8,
                                                  borderRadius: 4,
                                                  backgroundColor: theme.palette.grey[200],
                                                  '& .MuiLinearProgress-bar': {
                                                       backgroundColor: colors[index % colors.length],
                                                       borderRadius: 4,
                                                  },
                                             }}
                                        />

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                             <Typography
                                                  variant='caption'
                                                  color='text.secondary'
                                             >
                                                  {dependente.percentual.toFixed(1)}% do total
                                             </Typography>
                                        </Box>
                                   </Box>
                              ))}

                              {data.length > 0 && (
                                   <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                                        <Typography
                                             variant='caption'
                                             color='text.secondary'
                                        >
                                             Total: {formatCurrency(data.reduce((acc, item) => acc + item.total_gastos, 0))}
                                        </Typography>
                                   </Box>
                              )}
                         </Box>
                    )}
               </CardContent>
          </Card>
     );
};
